import { useState } from "react";
import Sheet from "./Sheet";
import { blockUser, reportUser } from "../lib/api";
import { friendly } from "../lib/errors";
import { REPORT_REASONS } from "../lib/types";

/**
 * Block / report flow. Reporting also blocks (server behavior). onDone fires
 * after a successful action so callers can navigate away / refresh lists.
 */
export default function ReportSheet({
  targetUid,
  targetName,
  open,
  onClose,
  onDone,
}: {
  targetUid: string;
  targetName: string;
  open: boolean;
  onClose: () => void;
  onDone: (action: "blocked" | "reported") => void;
}) {
  const [mode, setMode] = useState<"menu" | "report">("menu");
  const [reason, setReason] = useState(REPORT_REASONS[0].value);
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setMode("menu");
    setDetails("");
    setError(null);
  }

  async function doBlock() {
    setBusy(true);
    setError(null);
    try {
      await blockUser({ targetUid });
      onDone("blocked");
      reset();
    } catch (e) {
      setError(friendly(e));
    } finally {
      setBusy(false);
    }
  }

  async function doReport() {
    setBusy(true);
    setError(null);
    try {
      await reportUser({ targetUid, reason, details: details.trim() || undefined });
      onDone("reported");
      reset();
    } catch (e) {
      setError(friendly(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet open={open} onClose={() => { reset(); onClose(); }} title={mode === "menu" ? targetName : `Report ${targetName}`}>
      {mode === "menu" ? (
        <>
          <button className="btn btn-secondary" disabled={busy} onClick={doBlock}>
            Block {targetName}
          </button>
          <p className="muted" style={{ fontSize: 13, margin: "6px 2px" }}>
            They won't see you and you won't see them. Any conversation is removed for both of you.
          </p>
          <button className="btn btn-danger" disabled={busy} onClick={() => setMode("report")}>
            Report {targetName}
          </button>
          <p className="muted" style={{ fontSize: 13, margin: "6px 2px" }}>
            Reporting also blocks them. Our team reviews every report.
          </p>
        </>
      ) : (
        <>
          {REPORT_REASONS.map((r) => (
            <label key={r.value} className="radio-row">
              <input type="radio" name="reason" checked={reason === r.value} onChange={() => setReason(r.value)} />
              {r.label}
            </label>
          ))}
          <textarea
            value={details}
            maxLength={1000}
            placeholder="Anything else we should know? (optional)"
            onChange={(e) => setDetails(e.target.value)}
          />
          <button className="btn btn-danger" disabled={busy} onClick={doReport}>
            {busy ? "Sending…" : "Submit report"}
          </button>
          <button className="btn btn-ghost" disabled={busy} onClick={() => setMode("menu")}>
            Back
          </button>
        </>
      )}
      {error && <div className="error">{error}</div>}
    </Sheet>
  );
}
